import React, { useState } from "react";

const Django = () => {
  const [expandedSections, setExpandedSections] = useState({});

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const syllabusData = {
    courseOverview: {
      title: "Course Overview",
      content: (
        <>
          <p className="font-bold text-sm">Objective:</p> Learn to build robust
          web applications using the Django framework.
          <p className="font-bold text-sm">Duration:</p> 45 days, 1 hour per
          day.
          <p className="font-bold text-sm">Prerequisites:</p> Basic knowledge of
          Python, HTML, and CSS; familiarity with command-line tools.
          <p className="font-bold text-sm">Tools Needed:</p> Python 3.x, Django,
          a code editor (e.g., VS Code), PostgreSQL or SQLite, and a web
          browser.
        </>
      ),
    },
    dailySchedule: {
      title: "Daily Schedule",
      days: [
        {
          day: "Day 1: Introduction to Django",
          objective:
            "Understand Django and set up the development environment.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>What is Django? Why use it?</li>
                <li>Django history and features.</li>
                <li>Installing Django and setting up a virtual environment.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Create a new Django project.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 2: Django Project Structure",
          objective: "Explore the Django project structure.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Understanding settings.py, urls.py, and wsgi.py.</li>
                <li>Creating an app with manage.py.</li>
                <li>Project vs. app structure.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Create a new app and explore its files.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 3: Models and Database Setup",
          objective: "Learn to define models and configure the database.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Introduction to ORM (Object-Relational Mapping).</li>
                <li>Defining models in models.py.</li>
                <li>Running migrations with migrate and makemigrations.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Create a model for a simple blog post.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 4: Admin Interface",
          objective: "Set up and customize the Django admin panel.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Registering models with admin.py.</li>
                <li>Customizing the admin interface.</li>
                <li>Adding superuser and managing data.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Register a model and add data via the admin panel.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 5: Views – Basics",
          objective: "Understand and create basic views in Django.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>What are views? Function-based views.</li>
                <li>Returning HTTP responses.</li>
                <li>Connecting views to URLs.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Create a view to display a "Hello World" page.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 6: URLs and Routing",
          objective: "Learn to configure URL routing in Django.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>URL patterns and path() function.</li>
                <li>Namespacing URLs.</li>
                <li>Passing parameters in URLs.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Set up URL routing for a simple page.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 7: Templates – Basics",
          objective: "Learn to use Django templates.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Introduction to the template system.</li>
                <li>Creating and rendering HTML templates.</li>
                <li>Using template variables and filters.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Create a template to display dynamic content.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 8: Template Inheritance",
          objective: "Explore template inheritance and blocks.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Using template inheritance to extend base templates.</li>
                <li>Creating reusable base templates.</li>
                <li>Overriding blocks in child templates.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Build a base template with child templates.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 9: Static Files",
          objective: "Manage static files like CSS and JavaScript.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Configuring static files in settings.py.</li>
                <li>Using the static tag to load static files.</li>
                <li>Serving static files in development.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Add CSS styling to a template.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 10: Media Files",
          objective: "Handle user-uploaded media files.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Configuring media settings.</li>
                <li>Uploading files via forms.</li>
                <li>Displaying uploaded media.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Create a form to upload and display an image.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 11: Forms – Basics",
          objective: "Learn to create and handle forms in Django.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Creating forms with Django forms.</li>
                <li>Rendering forms in templates.</li>
                <li>Handling form submissions.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Build a contact form.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 12: Forms – ModelForms",
          objective: "Use ModelForms for database integration.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Introduction to ModelForms.</li>
                <li>Creating ModelForms from models.</li>
                <li>Saving form data to the database.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Create a ModelForm for a blog post.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 13: QuerySets and Database Queries",
          objective: "Learn to query the database with Django ORM.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Introduction to QuerySets.</li>
                <li>Filtering, ordering, and slicing data.</li>
                <li>Using aggregate functions.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Query the database for blog posts.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 14: Relationships in Models",
          objective: "Understand model relationships (FK, M2M).",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>ForeignKey and ManyToManyField.</li>
                <li>Creating relationships between models.</li>
                <li>Querying related objects.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Add a category relationship to the blog model.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 15: Class-Based Views",
          objective: "Learn to use class-based views.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Introduction to class-based views (CBVs).</li>
                <li>Common CBVs (ListView, DetailView).</li>
                <li>Customizing CBVs.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Implement a ListView for blog posts.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 16: Generic Views",
          objective: "Explore Django’s generic class-based views.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Using CreateView, UpdateView, DeleteView.</li>
                <li>Configuring templates for generic views.</li>
                <li>Handling form submissions with generic views.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Create a CRUD interface with generic views.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 17: User Authentication – Basics",
          objective: "Implement user authentication in Django.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Using Django’s built-in auth system.</li>
                <li>Login, logout, and signup views.</li>
                <li>Configuring authentication URLs.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Set up a login and logout system.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 18: User Authentication – Permissions",
          objective: "Manage user permissions and groups.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Adding permissions to models.</li>
                <li>Using groups and user permissions.</li>
                <li>Restricting access with decorators.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Restrict a view to admin users.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 19: Sessions and Cookies",
          objective: "Learn to manage sessions and cookies.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Understanding sessions in Django.</li>
                <li>Setting and retrieving cookies.</li>
                <li>Session security considerations.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Store user preferences in a session.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 20: Middleware",
          objective: "Understand and customize middleware.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>What is middleware?</li>
                <li>Built-in middleware classes.</li>
                <li>Creating custom middleware.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Write a custom middleware to log requests.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 21: Django REST Framework – Setup",
          objective: "Set up Django REST Framework (DRF) for APIs.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Introduction to DRF.</li>
                <li>Installing and configuring DRF.</li>
                <li>Creating a basic API endpoint.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Set up DRF and create a simple endpoint.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 22: Serializers in DRF",
          objective: "Learn to use serializers for data conversion.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>What are serializers?</li>
                <li>Creating ModelSerializers.</li>
                <li>Handling validation and deserialization.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Serialize a blog post model.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 23: ViewSets and Routers",
          objective: "Use ViewSets and Routers for API development.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Introduction to ViewSets.</li>
                <li>Configuring routers for URL patterns.</li>
                <li>Implementing CRUD operations.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Create a ViewSet for blog posts.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 24: Authentication in DRF",
          objective: "Secure APIs with authentication.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Token authentication.</li>
                <li>Session authentication.</li>
                <li>Using permissions and throttling.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Add token authentication to an API.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 25: Testing Django Applications",
          objective: "Learn to test Django apps.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Writing unit tests with Django TestCase.</li>
                <li>Testing views and models.</li>
                <li>Running tests with manage.py.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Write a test for a view.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 26: Django Signals",
          objective: "Understand and use Django signals.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>What are signals?</li>
                <li>Built-in signals (pre_save, post_save).</li>
                <li>Creating custom signals.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Implement a signal to log model changes.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 27: Customizing User Model",
          objective: "Extend the default user model.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>AbstractUser vs. AbstractBaseUser.</li>
                <li>Creating a custom user model.</li>
                <li>Migrating to a custom user model.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Create a custom user model with email as username.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 28: Email Sending in Django",
          objective: "Learn to send emails with Django.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Configuring email settings.</li>
                <li>Using send_mail() and EmailMultiAlternatives.</li>
                <li>Integrating with SMTP servers.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Send a test email from a view.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 29: Django Security Best Practices",
          objective: "Implement security measures in Django.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>
                  Cross-Site Scripting (XSS) and Cross-Site Request Forgery
                  (CSRF).
                </li>
                <li>Using HTTPS and secure cookies.</li>
                <li>SQL injection prevention with ORM.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Configure CSRF protection for a form.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 30: Performance Optimization",
          objective: "Optimize Django application performance.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Caching with Django cache framework.</li>
                <li>Query optimization and select_related/prefetch_related.</li>
                <li>Using database indexes.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Implement caching for a view.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 31: Deployment – Local Setup",
          objective: "Prepare the app for local deployment.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Configuring production settings.</li>
                <li>Collecting static files.</li>
                <li>Running with Gunicorn.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Run the app locally with Gunicorn.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 32: Deployment – Heroku Basics",
          objective: "Deploy a Django app to Heroku.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Introduction to Heroku.</li>
                <li>Setting up a Heroku app.</li>
                <li>Deploying with git push.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Deploy a simple app to Heroku.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 33: Deployment – Database Configuration",
          objective: "Configure a production database on Heroku.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Using PostgreSQL on Heroku.</li>
                <li>Migrating the database.</li>
                <li>Securing database credentials.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Connect the app to a Heroku PostgreSQL database.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 34: Deployment – Domain and SSL",
          objective: "Set up a custom domain and SSL.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Adding a custom domain to Heroku.</li>
                <li>Configuring SSL with Heroku.</li>
                <li>Testing the deployed app.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Configure a custom domain with SSL.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 35: CI/CD with Django",
          objective: "Set up CI/CD pipelines for Django.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Introduction to CI/CD.</li>
                <li>Using GitHub Actions for Django.</li>
                <li>Automating tests and deployments.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Set up a basic CI/CD pipeline.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 36: Final Project – Planning",
          objective: "Plan the final project.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min discussion):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Overview of the final project.</li>
                <li>Suggested project ideas (e.g., Blog, E-commerce).</li>
                <li>Planning features and database design.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Create a project plan and wireframe.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 37: Final Project – Setup and Models",
          objective: "Set up the project and define models.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Project setup and app creation.</li>
                <li>Designing and creating models.</li>
                <li>Running migrations.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Implement the project models.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 38: Final Project – Views and URLs",
          objective: "Develop views and URL routing.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Creating function-based and class-based views.</li>
                <li>Setting up URL patterns.</li>
                <li>Testing views.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Implement views and URLs for the project.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 39: Final Project – Templates and Static Files",
          objective: "Design templates and add static files.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Creating reusable templates.</li>
                <li>Adding CSS and JavaScript.</li>
                <li>Testing the frontend.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Design the project templates.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 40: Final Project – Forms and Authentication",
          objective: "Add forms and user authentication.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Implementing ModelForms.</li>
                <li>Adding login/signup functionality.</li>
                <li>Securing views.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Add authentication to the project.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 41: Final Project – API Integration",
          objective: "Integrate APIs with Django REST Framework.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Setting up DRF.</li>
                <li>Creating serializers and viewsets.</li>
                <li>Testing the API.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Implement an API for the project.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 42: Final Project – Testing",
          objective: "Test the final project.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Writing unit and integration tests.</li>
                <li>Testing views, forms, and APIs.</li>
                <li>Fixing bugs.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Write and run tests for the project.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 43: Final Project – Optimization",
          objective: "Optimize the final project.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Query optimization.</li>
                <li>Implementing caching.</li>
                <li>Reducing page load times.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Optimize a view or API endpoint.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 44: Final Project – Deployment Prep",
          objective: "Prepare for deployment.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Configuring production settings.</li>
                <li>Collecting static files.</li>
                <li>Testing the production build.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Prepare the project for deployment.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 45: Final Project – Presentation",
          objective: "Present the final project.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Preparing a live demo.</li>
                <li>Discussing challenges and solutions.</li>
                <li>Presenting to peers.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Present the completed project.</li>
              </ul>
            </>
          ),
        },
      ],
    },
    learningOutcomes: {
      title: "Learning Outcomes",
      content: (
        <>
          <p className="text-sm">
            By the end of this course, learners will be able to:
          </p>
          <ul className="list-disc pl-4 text-xs">
            <li>Build and deploy web applications using Django.</li>
            <li>Manage databases with Django ORM.</li>
            <li>Implement authentication, forms, and APIs.</li>
            <li>Optimize and secure Django applications.</li>
          </ul>
        </>
      ),
    },
    finalProjectIdeas: {
      title: "Suggested Final Project Ideas",
      content: (
        <>
          <ul className="list-disc pl-4 text-xs">
            <li>
              <span className="font-bold text-sm">Blog Platform:</span> A
              full-featured blog with user authentication and comments.
            </li>
            <li>
              <span className="font-bold text-sm">E-commerce Store:</span> An
              online store with product listings, cart, and checkout.
            </li>
            <li>
              <span className="font-bold text-sm">Task Management App:</span> A
              web app for managing tasks with user roles and APIs.
            </li>
          </ul>
        </>
      ),
    },
    additionalNotes: {
      title: "Additional Notes",
      content: (
        <>
          <ul className="list-disc pl-4 text-xs">
            <li>Encourage practice with Django projects and documentation.</li>
            <li>Provide resources (e.g., Django docs, tutorials).</li>
            <li>Use discussion time for Q&A and troubleshooting.</li>
          </ul>
          <p className="text-sm">
            This syllabus progresses from basics to advanced topics, ensuring
            practical skills in Django development over 45 days.
          </p>
        </>
      ),
    },
  };

  // Function to handle enrollment (replace with actual enrollment logic)
  const handleEnroll = () => {
    alert("Enroll Now clicked! (Add your enrollment logic here)");
  };

  return (
    <div className="min-h-screen bg-gray-100 font-sans">
      {/* Hero Section */}
      <header className="bg-gradient-to-r from-orange-400 to-indigo-600 py-8 text-center">
        <h1 className="text-2xl font-bold mb-1 text-white">Django Syllabus</h1>
        <p className="text-sm text-white">
          Master Web Development with Django (45 Hours – 1 Hour Per Day)
        </p>
      </header>

      {/* Training Availability Banner */}
      <div className="fixed top-34 left-80v right-4 bg-yellow-200 border border-yellow-400 rounded-lg p-3 text-center shadow-md z-10">
        <h2 className="text-sm font-semibold text-indigo-800">
          Training Available
        </h2>
        <p className="text-xs text-gray-700">Online Class | Physical Class</p>
        <button
          onClick={handleEnroll}
          className="mt-2 px-4 py-1 bg-orange-600 text-white text-xs rounded-lg hover:bg-orange-700 transition duration-300"
        >
          Enroll Now
        </button>
      </div>

      <div className="max-w-4xl mx-auto p-2 pt-16 pb-16">
        {/* Course Overview */}
        <div className="mb-2 border border-gray-200 rounded-lg">
          <h2
            onClick={() => toggleSection("courseOverview")}
            className="bg-gray-100 p-1 cursor-pointer flex justify-between items-center text-sm font-semibold text-gray-700 hover:bg-gray-200"
          >
            {syllabusData.courseOverview.title}
            <span>{expandedSections["courseOverview"] ? "▲" : "▼"}</span>
          </h2>
          {expandedSections["courseOverview"] && (
            <div className="p-1 bg-white text-gray-600 leading-tight">
              {syllabusData.courseOverview.content}
            </div>
          )}
        </div>

        {/* Daily Schedule */}
        <div className="mb-2 border border-gray-200 rounded-lg">
          <h2
            onClick={() => toggleSection("dailySchedule")}
            className="bg-gray-100 p-1 cursor-pointer flex justify-between items-center text-sm font-semibold text-gray-700 hover:bg-gray-200"
          >
            {syllabusData.dailySchedule.title}
            <span>{expandedSections["dailySchedule"] ? "▲" : "▼"}</span>
          </h2>
          {expandedSections["dailySchedule"] && (
            <div className="p-1 bg-white">
              {syllabusData.dailySchedule.days.map((day, index) => (
                <div
                  key={index}
                  className="mb-1 border border-gray-100 rounded-lg"
                >
                  <h3
                    onClick={() => toggleSection(`day${index}`)}
                    className="bg-gray-50 p-1 cursor-pointer flex justify-between items-center text-xs font-medium text-gray-600 hover:bg-gray-100"
                  >
                    {day.day}
                    <span>{expandedSections[`day${index}`] ? "▲" : "▼"}</span>
                  </h3>
                  {expandedSections[`day${index}`] && (
                    <div className="p-1 bg-white text-gray-600 leading-tight">
                      <p className="font-bold text-sm">Objective:</p>{" "}
                      {day.objective}
                      {day.topics}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Learning Outcomes */}
        <div className="mb-2 border border-gray-200 rounded-lg">
          <h2
            onClick={() => toggleSection("learningOutcomes")}
            className="bg-gray-100 p-1 cursor-pointer flex justify-between items-center text-sm font-semibold text-gray-700 hover:bg-gray-200"
          >
            {syllabusData.learningOutcomes.title}
            <span>{expandedSections["learningOutcomes"] ? "▲" : "▼"}</span>
          </h2>
          {expandedSections["learningOutcomes"] && (
            <div className="p-1 bg-white text-gray-600 leading-tight">
              {syllabusData.learningOutcomes.content}
            </div>
          )}
        </div>

        {/* Final Project Ideas */}
        <div className="mb-2 border border-gray-200 rounded-lg">
          <h2
            onClick={() => toggleSection("finalProjectIdeas")}
            className="bg-gray-100 p-1 cursor-pointer flex justify-between items-center text-sm font-semibold text-gray-700 hover:bg-gray-200"
          >
            {syllabusData.finalProjectIdeas.title}
            <span>{expandedSections["finalProjectIdeas"] ? "▲" : "▼"}</span>
          </h2>
          {expandedSections["finalProjectIdeas"] && (
            <div className="p-1 bg-white text-gray-600 leading-tight">
              {syllabusData.finalProjectIdeas.content}
            </div>
          )}
        </div>

        {/* Additional Notes */}
        <div className="mb-2 border border-gray-200 rounded-lg">
          <h2
            onClick={() => toggleSection("additionalNotes")}
            className="bg-gray-100 p-1 cursor-pointer flex justify-between items-center text-sm font-semibold text-gray-700 hover:bg-gray-200"
          >
            {syllabusData.additionalNotes.title}
            <span>{expandedSections["additionalNotes"] ? "▲" : "▼"}</span>
          </h2>
          {expandedSections["additionalNotes"] && (
            <div className="p-1 bg-white text-gray-600 leading-tight">
              {syllabusData.additionalNotes.content}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Django;
